import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ISafetyEventDetail } from '@d-orca/shared/safety';
import { closeSafetyEventApi, fetchSafetyEventDetail } from '@apis/monitor';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { applyStreamEvent } from '@store/slices/monitor.slice';
import {
  selectIsSafetyEventActive,
  selectProject,
  selectSiteId,
} from '@store/selectors/monitor.selectors';
import { Modal } from '@components/ui/Modal/Modal';
import { Button } from '@components/ui/Button/Button';
import { Loader } from '@components/ui/Loader/Loader';
import { AlertCircleFilledIcon } from '@utils/icons';
import { formatProjectDateTime } from '@utils/formatProjectDate';
import { professionDisplayText } from '@utils/professionDisplay';
import styles from './SafetyAlertDialog.module.css';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';

interface ISafetyAlertDialogProps {
  eventId: string;
  onClose: () => void;
}

/**
 * A safety alert's detail, opened from its row — Trusstor's safety event
 * popup. The row's minimal event lacks the tag and area, so the dialog reads
 * the event by id. End Event closes it upstream; the server says who may
 * (`canEnd`), and the live feed says whether it is still open — the fetched
 * detail goes stale the moment anyone closes it. The closed event is merged
 * into the monitor state so the row moves to the log at once, not only when
 * the stream echoes it.
 */
export function SafetyAlertDialog({ eventId, onClose }: ISafetyAlertDialogProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const siteId = useAppSelector(selectSiteId);
  const tz = useAppSelector(selectProject)?.tz;
  const isActive = useAppSelector((state) => selectIsSafetyEventActive(state, eventId));

  // Never served from cache: a safety event's state is live, and a cached
  // "active" copy would offer End Event on an event already closed.
  const detail = useQuery({
    queryKey: ['monitor', 'safetyEvent', siteId, eventId],
    queryFn: ({ signal }) => fetchSafetyEventDetail(siteId!, eventId, signal),
    enabled: !!siteId,
    gcTime: 0,
  });
  const endEvent = useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: () => closeSafetyEventApi(siteId!, eventId),
    onSuccess: ({ event }) => {
      if (event) dispatch(applyStreamEvent({ channel: 'safety', data: { safetyEvents: [event] } }));
      onClose();
    },
  });

  const canEnd = !!detail.data?.canEnd && isActive;

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={
        <span className={styles.title}>
          <span className={styles.titleIcon} aria-hidden="true">
            <AlertCircleFilledIcon />
          </span>
          {t('monitor.safetyAlertDialog.title')}
        </span>
      }
      footer={
        canEnd ? (
          <>
            {endEvent.isError && (
              <span className={styles.error} role="alert">
                {t('monitor.safetyAlertDialog.endFailed')}
              </span>
            )}
            <span className={styles.footerSpacer} />
            <Button variant="secondary" size="md" onClick={onClose}>
              {t('monitor.safetyAlertDialog.cancel')}
            </Button>
            <Button
              variant="primary"
              size="md"
              disabled={endEvent.isPending}
              onClick={() => endEvent.mutate()}
            >
              {t('monitor.safetyAlertDialog.endEvent')}
            </Button>
          </>
        ) : undefined
      }
    >
      <SafetyAlertDialogBody
        event={detail.data?.event}
        failed={detail.isError}
        tz={tz}
      />
    </Modal>
  );
}

interface ISafetyAlertDialogBodyProps {
  event: ISafetyEventDetail | undefined;
  failed: boolean;
  tz: string | undefined;
}

function SafetyAlertDialogBody({ event, failed, tz }: ISafetyAlertDialogBodyProps) {
  const { t } = useTranslation();
  if (event) return <SafetyEventFields event={event} tz={tz} />;
  if (failed) {
    return (
      <p className={styles.error} role="alert">
        {t('monitor.safetyAlertDialog.loadFailed')}
      </p>
    );
  }
  return (
    <div className={styles.loading} role="status" aria-busy="true">
      <Loader size={24} />
    </div>
  );
}

interface ISafetyEventFieldsProps {
  event: ISafetyEventDetail;
  tz: string | undefined;
}

function SafetyEventFields({ event, tz }: ISafetyEventFieldsProps) {
  const { t } = useTranslation();
  // Project zone or nothing — same rule as the row: never stamp a safety
  // event in the viewer's zone.
  const formatTimestamp = (timestamp: number | null) =>
    timestamp !== null && tz ? formatProjectDateTime(new Date(timestamp).toISOString(), tz) : null;

  const location = [event.floorNick, ...event.areaNicks].filter(Boolean).join(', ');
  const tagOwner =
    event.tagOwner && event.phoneNumber
      ? `${event.tagOwner} (${event.phoneNumber})`
      : event.tagOwner ?? event.phoneNumber;
  const profession = event.profession ? professionDisplayText(t, event.profession) : null;

  return (
    <div className={styles.fields}>
      <p className={styles.type}>{t(`monitor.safetyViolationType.${event.type}`)}</p>
      <DetailField
        label={t('monitor.safetyAlertDialog.openingTime')}
        value={formatTimestamp(event.timestampCreated)}
      />
      <div className={styles.fieldRow}>
        <DetailField label={t('monitor.safetyAlertDialog.location')} value={location} />
        <DetailField label={t('monitor.safetyAlertDialog.tagId')} value={event.tagNick} />
      </div>
      <DetailField label={t('monitor.safetyAlertDialog.tagOwner')} value={tagOwner} />
      <DetailField label={t('monitor.safetyAlertDialog.profession')} value={profession} />
      <DetailField
        label={t('monitor.safetyAlertDialog.eventClosed')}
        value={formatTimestamp(event.timestampClosed)}
      />
    </div>
  );
}

interface IDetailFieldProps {
  label: string;
  value: string | null;
}

/** One label + value line; hidden when the event has no value for it. */
function DetailField({ label, value }: IDetailFieldProps) {
  if (!value) return null;
  return (
    <span className={styles.field}>
      <span>{label}</span>
      <span className={styles.fieldValue}>{value}</span>
    </span>
  );
}
